import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const API_BASE = "https://nissi-india.com/api/ownVehicleReport";

const toStr = (v) => String(v ?? "").trim();
const toNumStr = (v, d = "0.00") => {
  if (v === null || v === undefined || v === "") return d;
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const pick = (...vals) => {
  for (const v of vals) {
    const s = String(v ?? "").trim();
    if (s) return s;
  }
  return "";
};

export default function OwnEquipmentReport() {
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ vehicleId from navigate state
  const vehicleIdFromState = location?.state?.vehicleId;

  // ✅ fallback: allow opening page directly with query param
  const vehicleIdFromQuery = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("vehicleId");
  }, []);

  const vehicleId = vehicleIdFromState || vehicleIdFromQuery;

  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [equipmentData, setEquipmentData] = useState({
    entryDate: "",
    equipmentBranch: "",
    hypothecationWith: "",

    equipmentSubtype: "",
    store: "",
    equipmentCode: "",
    equipmentName: "",
    equipmentMake: "",
    equipmentRegNo: "",
    equipmentRegDate: "",
    model: "",
    equipmentUsageCost: "0.00",
    equipmentDescription: "",
    equipmentCondition: "",
    equipmentBasis: "",

    insuranceCompany: "",
    insuranceNumber: "",
    insuranceDate: "",
    insuranceAmount: "0.00",
    insurancePremiumPerYear: "0",
    insuranceRenewalDate: "",
    currentCummulativeHMR: "",
    lastUpdatedHMR: "",
    currentCummulativeKMR: "",
    lastUpdatedKMR: "",
    manPowerCost: "0.00",

    chassisNumber: "",
    fuelType: "",
    engineSlNo: "",
    equipmentSlNo: "",
    freeServiceDate: "",

    fcDate: "",
    fcRenewalDate: "",

    purchasedFrom: "",
    purchasedDate: "",
    purchasedAmount: "0.00",
    invoiceNumber: "",
    remarks: "",

    roadTaxFromDate: "",
    roadTaxToDate: "",
    permitDate: "",
    permitExpireDate: "",
    rcBookNumber: "",
  });

  useEffect(() => {
    const fetchDetails = async () => {
      if (!vehicleId) {
        setErrMsg("Vehicle ID not found. Please open this report from listing (Print/View).");
        return;
      }

      try {
        setLoading(true);
        setErrMsg("");

        // ✅ API call
        const { data } = await axios.get(`${API_BASE}/details`, {
          params: { vehicleId },
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load report details");
        }

        // ✅ Your backend might return: data.row OR data.data OR data.details
        const r = data?.row || data?.data || data?.details || {};

        // ✅ Map backend fields -> report fields
        // Adjust keys if your API returns different names.
        const mapped = {
          entryDate: pick(r.fld_entrydate, r.entryDate),
          equipmentBranch: pick(r.fld_branchname, r.equipmentBranch),
          hypothecationWith: pick(r.fld_hypothecationwith, r.hypothecationWith),

          equipmentSubtype: pick(r.fld_vehicle_subtypename, r.fld_vehiclesubtype, r.equipmentSubtype),
          store: pick(r.fld_store, r.storename, r.store),
          equipmentCode: pick(r.fld_vehicle_code, r.equipmentCode),
          equipmentName: pick(r.fld_vehicle_name, r.equipmentName),
          equipmentMake: pick(r.fld_vehiclemake, r.fld_make, r.equipmentMake),
          equipmentRegNo: pick(r.fld_vehicle_regno, r.equipmentRegNo),
          equipmentRegDate: pick(r.fld_regdate, r.fld_vehicle_regdate, r.equipmentRegDate),
          model: pick(r.fld_vehicle_model, r.model),
          equipmentUsageCost: toNumStr(pick(r.fld_usagecost, r.equipmentUsageCost), "0.00"),
          equipmentDescription: pick(r.fld_desc, r.fld_description, r.equipmentDescription),
          equipmentCondition: pick(r.fld_vehicle_condition, r.equipmentCondition),
          equipmentBasis: pick(r.fld_vehicle_basisname, r.fld_basisname, r.equipmentBasis),

          insuranceCompany: pick(r.fld_insurance_company, r.insuranceCompany),
          insuranceNumber: pick(r.fld_insurance_no, r.fld_insurance_number, r.insuranceNumber),
          insuranceDate: pick(r.fld_insurance_date, r.insuranceDate),
          insuranceAmount: toNumStr(pick(r.fld_insurance_amount, r.insuranceAmount), "0.00"),
          insurancePremiumPerYear: pick(r.fld_insurance_premiumperyear, r.insurancePremiumPerYear, "0"),
          insuranceRenewalDate: pick(r.fld_insurance_renewal_date, r.insuranceRenewalDate),

          currentCummulativeHMR: pick(r.fld_cumHMR, r.currentCummulativeHMR),
          lastUpdatedHMR: pick(r.fld_lastupdatedhmr, r.lastUpdatedHMR, "0"),
          currentCummulativeKMR: pick(r.fld_cumKMR, r.currentCummulativeKMR),
          lastUpdatedKMR: pick(r.fld_lastupdatedkmr, r.lastUpdatedKMR, "0"),
          manPowerCost: toNumStr(pick(r.fld_manpowercost, r.manPowerCost), "0.00"),

          chassisNumber: pick(r.fld_Chassisno, r.fld_chassisno, r.chassisNumber),
          fuelType: pick(r.fld_fueltypename, r.fld_fueltype, r.fuelType),
          engineSlNo: pick(r.fld_engineSLNO, r.engineSlNo),
          equipmentSlNo: pick(r.fld_equipmentSLNO, r.equipmentSlNo),
          freeServiceDate: pick(r.fld_freeservicedate, r.freeServiceDate),

          fcDate: pick(r.fld_fcdate, r.fcDate),
          fcRenewalDate: pick(r.fld_fc_renewal_date, r.fcRenewalDate),

          purchasedFrom: pick(r.fld_purchased_from, r.purchasedFrom),
          purchasedDate: pick(r.fld_purchaseddate, r.purchasedDate),
          purchasedAmount: toNumStr(pick(r.fld_purchased_amt, r.purchasedAmount), "0.00"),
          invoiceNumber: pick(r.fld_invoiceno, r.invoiceNumber),
          remarks: pick(r.fld_remarks, r.remarks),

          roadTaxFromDate: pick(r.fld_roadtaxfrom, r.roadTaxFromDate),
          roadTaxToDate: pick(r.fld_roadtaxto, r.roadTaxToDate),
          permitDate: pick(r.fld_permitdate, r.permitDate),
          permitExpireDate: pick(r.fld_permitexpiredate, r.permitExpireDate),
          rcBookNumber: pick(r.fld_rcbookno, r.rcBookNumber),
        };

        setEquipmentData((prev) => ({ ...prev, ...mapped }));
      } catch (err) {
        console.error(err);
        const msg = err?.response?.data?.message || err?.message || "Failed to load report";
        setErrMsg(msg);
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [vehicleId]);

  return (
    <div className="w-full bg-white print:max-w-none print:mx-0">
      {/* Print Styles */}
      <style jsx>{`
        @media print {
          body { margin: 0; padding: 10px; font-size: 11pt; }
          .no-print { display: none !important; }
          table { border-collapse: collapse; }
          th, td { border: 1px solid #000 !important; padding: 3px !important; }
          .print-portrait { 
            size: A4 portrait; 
            margin: 0.5in;
          }
        }
        @page {
          size: A4 portrait;
          margin: 0.5in;
        }
      `}</style>

      {/* Top Bar (no-print) */}
      <div className="no-print flex items-center justify-between p-3 border-b">
        <div className="text-sm">
          <span className="font-semibold">Vehicle ID:</span> {vehicleId || "-"}
          {loading && <span className="ml-3">Loading...</span>}
          {errMsg && <span className="ml-3 text-red-600">{errMsg}</span>}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded"
          >
            Back
          </button>
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
            disabled={loading}
          >
            🖨️ Print
          </button>
        </div>
      </div>

      <div className="p-4 print:p-2 print-portrait max-w-4xl mx-auto">
        <div className="border-2 border-black">
          {/* Header Section */}
          <div className="border-b-2 border-black">
            <div className="flex items-center p-3">
              <div className="w-24 h-20 border-2 border-black flex items-center justify-center mr-4 bg-gray-800 text-white font-bold">
                <div className="text-center">
                  <div className="text-xs">NISSI</div>
                  <div className="text-xs">INFOTECH</div>
                  <div className="text-xs">PVT LTD</div>
                </div>
              </div>

              <div className="flex-1 text-center">
                <h1 className="text-xl font-bold">Nissi Infotech</h1>
                <div className="text-sm font-semibold">Nissi</div>
                <div className="text-sm font-semibold">CIVIL CONSTRUCTION</div>
                <div className="text-xs mt-1">
                  3/42-12, Nagappa colony, kavundampalayam-641030, Tamil Nadu, India.
                </div>
                <div className="text-xs">
                  Phone No: 9003744644&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  infracnsoft-implementation@nissiinfotech.in
                </div>
              </div>
            </div>
          </div>

          {/* Report Title */}
          <div className="text-center py-2 border-b-2 border-black bg-gray-100">
            <h2 className="text-lg font-bold">Own Equipment Report</h2>
          </div>

          {/* Entry Details */}
          <div className="border-b-2 border-black">
            <div className="grid grid-cols-2 text-sm">
              <div className="border-r border-black p-2">
                <div className="flex">
                  <span className="font-semibold w-32">Entry Date</span>
                  <span className="mx-2">:</span>
                  <span>{equipmentData.entryDate}</span>
                </div>
              </div>
              <div className="p-2">
                <div className="flex">
                  <span className="font-semibold w-40">Hypothecation with</span>
                  <span className="mx-2">:</span>
                  <span>{equipmentData.hypothecationWith}</span>
                </div>
              </div>
            </div>
            <div className="border-t border-black">
              <div className="p-2 text-sm">
                <div className="flex">
                  <span className="font-semibold w-32">Equipment Branch</span>
                  <span className="mx-2">:</span>
                  <span>{equipmentData.equipmentBranch}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-2">
            {/* Left Column */}
            <div className="border-r-2 border-black">
              <div className="bg-gray-100 p-2 border-b border-black">
                <h3 className="font-bold text-sm">EQUIPMENT GENERAL DETAILS</h3>
              </div>

              <div className="p-2 space-y-2 text-xs">
                {[
                  ["Equipment Subtype", equipmentData.equipmentSubtype],
                  ["Store", equipmentData.store],
                  ["Equipment code", equipmentData.equipmentCode],
                  ["Equipment Name", equipmentData.equipmentName],
                  ["Equipment Make", equipmentData.equipmentMake],
                  ["Equipment Reg No", equipmentData.equipmentRegNo],
                  ["Equipment Reg Date", equipmentData.equipmentRegDate],
                  ["Model", equipmentData.model],
                  ["Equipment Usage Cost", equipmentData.equipmentUsageCost],
                  ["Equipment Description", equipmentData.equipmentDescription],
                  ["Equipment Condition", equipmentData.equipmentCondition],
                  ["Equipment Basis", equipmentData.equipmentBasis],
                ].map(([label, value]) => (
                  <div className="flex" key={label}>
                    <span className="font-semibold w-32">{label}</span>
                    <span className="mx-2">:</span>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column */}
            <div>
              <div className="bg-gray-100 p-2 border-b border-black">
                <h3 className="font-bold text-sm">INSURANCE DETAILS</h3>
              </div>

              <div className="p-2 space-y-2 text-xs">
                {[
                  ["Insurance Company", equipmentData.insuranceCompany],
                  ["Insurance Number", equipmentData.insuranceNumber],
                  ["Insurance Date", equipmentData.insuranceDate],
                  ["Insurance Amount", equipmentData.insuranceAmount],
                  ["Insurance Premium per year", equipmentData.insurancePremiumPerYear],
                  ["Insurance Renewal Date", equipmentData.insuranceRenewalDate],
                  ["Current Cummulative HMR", equipmentData.currentCummulativeHMR],
                  ["Last Updated HMR", equipmentData.lastUpdatedHMR],
                  ["Current Cummulative KMR", equipmentData.currentCummulativeKMR],
                  ["Last Updated KMR", equipmentData.lastUpdatedKMR],
                  ["Man Power Cost", equipmentData.manPowerCost],
                ].map(([label, value]) => (
                  <div className="flex" key={label}>
                    <span className="font-semibold w-40">{label}</span>
                    <span className="mx-2">:</span>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Second Row */}
          <div className="grid grid-cols-2 border-t-2 border-black">
            <div className="border-r-2 border-black">
              <div className="bg-gray-100 p-2 border-b border-black">
                <h3 className="font-bold text-sm">EQUIPMENT PART DETAILS</h3>
              </div>

              <div className="p-2 space-y-2 text-xs">
                {[
                  ["Chassis Number", equipmentData.chassisNumber],
                  ["Fuel Type", equipmentData.fuelType],
                  ["Engine Sl No", equipmentData.engineSlNo],
                  ["Equipment Sl No", equipmentData.equipmentSlNo],
                  ["Free Service Date", equipmentData.freeServiceDate],
                ].map(([label, value]) => (
                  <div className="flex" key={label}>
                    <span className="font-semibold w-32">{label}</span>
                    <span className="mx-2">:</span>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="bg-gray-100 p-2 border-b border-black">
                <h3 className="font-bold text-sm">FC DETAILS</h3>
              </div>

              <div className="p-2 space-y-2 text-xs">
                {[
                  ["FC Date", equipmentData.fcDate],
                  ["FC Renewal Date", equipmentData.fcRenewalDate],
                ].map(([label, value]) => (
                  <div className="flex" key={label}>
                    <span className="font-semibold w-32">{label}</span>
                    <span className="mx-2">:</span>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Third Row */}
          <div className="grid grid-cols-2 border-t-2 border-black">
            <div className="border-r-2 border-black">
              <div className="bg-gray-100 p-2 border-b border-black">
                <h3 className="font-bold text-sm">CONSIGNEER (OR) PURCHASE DETAILS</h3>
              </div>

              <div className="p-2 space-y-2 text-xs">
                {[
                  ["Purchased From", equipmentData.purchasedFrom],
                  ["Purchased Date", equipmentData.purchasedDate],
                  ["Purchased Amount", equipmentData.purchasedAmount],
                  ["Invoice Number", equipmentData.invoiceNumber],
                  ["Remarks", equipmentData.remarks],
                ].map(([label, value]) => (
                  <div className="flex" key={label}>
                    <span className="font-semibold w-32">{label}</span>
                    <span className="mx-2">:</span>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <div className="bg-gray-100 p-2 border-b border-black">
                <h3 className="font-bold text-sm">TAX DETAILS</h3>
              </div>

              <div className="p-2 space-y-2 text-xs">
                {[
                  ["Road Tax From Date", equipmentData.roadTaxFromDate],
                  ["Road Tax To Date", equipmentData.roadTaxToDate],
                  ["Permit Date", equipmentData.permitDate],
                  ["Permit Expire Date", equipmentData.permitExpireDate],
                  ["RC Book Number", equipmentData.rcBookNumber],
                ].map(([label, value]) => (
                  <div className="flex" key={label}>
                    <span className="font-semibold w-32">{label}</span>
                    <span className="mx-2">:</span>
                    <span>{value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t-2 border-black p-2 text-xs flex justify-between items-center">
            <div>Report generated by ERP-Software - www.infracnsoft.com owned by www.nissiinfotech.com</div>
            <div className="font-bold">1</div>
          </div>
        </div>
      </div>
    </div>
  );
}
